import * as THREE from 'three'
import { Line2 } from 'three/addons/lines/Line2.js'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineGeometry } from 'three/addons/lines/LineGeometry.js'
import { useRouteEditorStore } from '@/stores/routeEditorStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'

export class MapRouteEditorPath {
  scene: THREE.Scene
  routeEditorStore: any
  mapStore: any

  // Waypoint markers
  waypointMarkers: THREE.Mesh[] = []

  // Lines connecting waypoints
  pathLines: Line2[] = []

  // Loop closing line (from last waypoint back to first)
  loopLine: Line2 | null = null

  // Colors
  waypointColor = 0xb8860b // Dark goldenrod
  lineColor = 0xb8860b // Dark goldenrod
  loopLineColor = 0xb8860b // Dark goldenrod (dimmer)

  // Line width
  lineWidth = 3

  constructor(scene: THREE.Scene) {
    this.scene = scene
    this.routeEditorStore = useRouteEditorStore()
    this.mapStore = useMapStore()
  }

  /**
   * Update the route editor path visualization
   * Called when routeEditorStore.redrawPath changes
   */
  update() {
    this.clearAll()

    if (!this.routeEditorStore.initialized) {
      return
    }

    const waypoints = this.routeEditorStore.waypoints

    // Draw waypoint markers
    for (let i = 0; i < waypoints.length; i++) {
      const wp = waypoints[i]
      this.addWaypointMarker(wp.x, wp.y, i === 0)
    }

    // Draw lines connecting waypoints
    for (let i = 1; i < waypoints.length; i++) {
      const prev = waypoints[i - 1]
      const curr = waypoints[i]
      this.addPathLine(prev.x, prev.y, curr.x, curr.y)
    }

    // Draw loop closing line (from last waypoint back to first) if 2+ waypoints
    if (waypoints.length >= 2) {
      const first = waypoints[0]
      const last = waypoints[waypoints.length - 1]
      this.addLoopLine(last.x, last.y, first.x, first.y)
    }
  }

  /**
   * Add a waypoint marker at the specified position
   */
  addWaypointMarker(x: number, y: number, isFirst: boolean) {
    const radius = isFirst ? 1.5 : 1
    const geometry = new THREE.CircleGeometry(radius, 16)
    const material = new THREE.MeshBasicMaterial({
      color: this.waypointColor,
      transparent: true,
      opacity: 0.9
    })
    const marker = new THREE.Mesh(geometry, material)
    marker.position.set(x, y, 0.15)
    marker.name = 'routeWaypointMarker'
    this.scene.add(marker)
    this.waypointMarkers.push(marker)
  }

  /**
   * Add a thick path line between two waypoints using Line2
   */
  addPathLine(x1: number, y1: number, x2: number, y2: number) {
    const geometry = new LineGeometry()
    geometry.setPositions([x1, y1, 0.1, x2, y2, 0.1])

    const material = new LineMaterial({
      color: this.lineColor,
      linewidth: this.lineWidth,
      transparent: true,
      opacity: 0.8,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight)
    })

    const line = new Line2(geometry, material)
    line.name = 'routePathLine'
    line.computeLineDistances()

    this.scene.add(line)
    this.pathLines.push(line)
  }

  /**
   * Add the loop closing line (from last waypoint back to first)
   */
  addLoopLine(x1: number, y1: number, x2: number, y2: number) {
    const geometry = new LineGeometry()
    geometry.setPositions([x1, y1, 0.1, x2, y2, 0.1])

    const material = new LineMaterial({
      color: this.loopLineColor,
      linewidth: this.lineWidth,
      transparent: true,
      opacity: 0.4,
      dashed: true,
      dashSize: 2,
      gapSize: 2,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight)
    })

    this.loopLine = new Line2(geometry, material)
    this.loopLine.name = 'routeLoopLine'
    this.loopLine.computeLineDistances()
    this.scene.add(this.loopLine)
  }

  /**
   * Clear all route editor visualizations
   */
  clearAll() {
    // Remove waypoint markers
    for (const marker of this.waypointMarkers) {
      this.scene.remove(marker)
      marker.geometry.dispose()
      if (marker.material instanceof THREE.Material) {
        marker.material.dispose()
      }
    }
    this.waypointMarkers = []

    // Remove path lines
    for (const line of this.pathLines) {
      this.scene.remove(line)
      line.geometry.dispose()
      if (line.material instanceof THREE.Material) {
        line.material.dispose()
      }
    }
    this.pathLines = []

    // Remove loop line
    if (this.loopLine) {
      this.scene.remove(this.loopLine)
      this.loopLine.geometry.dispose()
      if (this.loopLine.material instanceof THREE.Material) {
        this.loopLine.material.dispose()
      }
      this.loopLine = null
    }
  }

  /**
   * Dispose of all resources
   */
  dispose() {
    this.clearAll()
  }
}
